import { ReparoApi } from './index';

export type PeriodType = 'DAY' | 'WEEK' | 'MONTH' | 'QUARTER';

export type StrategyType =
    | 'BUY_FG'
    | 'WORK_ORDER'
    | 'WORK_ORDER_PLUS_STN'
    | 'WORK_ORDER_PLUS_RM_PO'
    | 'WORK_ORDER_PLUS_RM_PO_STN';

export type StrategyFeasibility = 'OK' | 'TIGHT' | 'INFEASIBLE';

export type ItemFeasibility =
    | 'OK'
    | 'TIGHT'
    | 'INFEASIBLE_PARTIAL'
    | 'INFEASIBLE_ALL'
    | 'UNSOURCEABLE'
    | 'COVERED';

export type ProjectionPeriod = {
    label: string;
    periodDateKey: string;
    opening: number;
    consumption: number;
    inTransit: number;
    // Gross net requirement for this anchor (before lead-time gating).
    requirement: number;
    // Lead-feasible portion of the requirement (only this carries forward).
    achievable: number;
    // Whether an order placed now can still land by this anchor.
    feasible: boolean;
    /** Lands by the bucket's anchor; false = lands inside the bucket but late. */
    onTime?: boolean;
    plannedReceipt: number;
    closing: number;
    target: number;
    isShort: boolean;
    doh: number | null;
    isBreach: boolean;
};

export type StrategyLeg = {
    legType: string;
    fromName: string;
    toName: string;
    leadTimeDays: number;
    breakdown: { production: number; qc: number; transit: number; delivery: number };
};

export type ActionTemplate = {
    actionId: string;
    type: string;
    target: Record<string, string | undefined>;
    qtyBasis: 'ABSOLUTE_FG' | 'PER_FG_BOM';
    qtyPerFg?: number;
    dependsOn: string[];
    leadTimeDays: number;
    triggerByDate: string | null;
    label: string;
};

export type ReplenishmentStrategy = {
    strategyId: string;
    type: StrategyType;
    label: string;
    vendorId: string | null;
    vendorName: string | null;
    totalLeadTimeDays: number;
    orderByDate: string | null;
    feasibility: StrategyFeasibility;
    maxQty: number | null;
    moq: number | null;
    costPerUnit: number | null;
    postRestockDoh: number | null;
    legs: StrategyLeg[];
    actions: ActionTemplate[];
    warnings: string[];
};

export type RecommendedAllocation = { strategyId: string; qty: number };

export type ReplenishmentPlanItem = {
    id: string;
    productId: string;
    sku: string | null;
    productName: string | null;
    currentStock: number;
    currentDoh: number | null;
    breachDate: string | null;
    shortfallQty: number;
    horizonRequirement: number;
    firstShortDate: string | null;
    targetDaysOfCover: number | null;
    feasibility: ItemFeasibility;
    projection: ProjectionPeriod[];
    strategies: ReplenishmentStrategy[];
    recommendedAllocation: RecommendedAllocation[];
    /** PRODUCE frame: the lead the dip was shifted by to test the period; null in SHORT. */
    leadDays: number | null;
};

export type ReplenishmentPlanSummary = {
    horizonPeriods: number;
    targetDaysOfCover: number;
    totalSkus: number;
    breachingSkus: number;
    unsourceableSkus: number;
    infeasibleSkus: number;
    actionableSkus: number;
    lockedSkus: number;
    coveredSkus: number;
    partialSkus: number;
};

export type ReplenishmentPlan = {
    id: string;
    merchantId: string;
    status: 'DRAFT' | 'APPROVED' | 'EXECUTING' | 'COMPLETED' | 'ARCHIVED';
    periodType: PeriodType;
    horizonPeriods: number;
    targetDaysOfCover: number | null;
    parentPlanId: string | null;
    summary: ReplenishmentPlanSummary | Record<string, never>;
    /** Run scope + demand basis the plan was computed with (restores the controls). */
    targetWindowStart: string | null;
    targetWindowEnd: string | null;
    frame: PlanFrame;
    /** The last dip date this run built nodes for — past targetWindowEnd by the longest lead in PRODUCE. */
    demandWindowEnd: string | null;
    /** PRODUCE: the ordering window the RM tab is bound to. */
    orderingDays: number | null;
    orderingWindowEnd: string | null;
    demandBasis: 'FORWARD' | 'BACKWARD' | null;
    drrWindowDays: number | null;
    /** The per-channel demand setup this run used; null = the single basis above. */
    demandPolicy: DemandPolicy | null;
    /** The (s, S) inputs this run used — flat trigger override and refill level (null = per-tier / refill-to-trigger). */
    targetDaysOfCoverOverride: number | null;
    orderUpToDaysOfCover: number | null;
    createdAt: string;
    items: ReplenishmentPlanItem[];
};

export type PlanFrame = 'SHORT' | 'PRODUCE';

export type CreateReplenishmentPlanRequest = {
    horizonPeriods?: number;
    periodType?: PeriodType;
    targetDaysOfCover?: number;
    orderUpToDaysOfCover?: number;
    productIds?: string[];
    categories?: string[];
    productGroups?: string[];
    wastagePercent?: number;
    leadTimeAggregate?: 'MIN' | 'MAX';
    parentPlanId?: string;
    // Scope the run to a single window (its date bounds).
    targetWindowStart?: string;
    targetWindowEnd?: string;
    /** SHORT: the window bounds the dip date. PRODUCE: it bounds the action date (dip − lead). */
    frame?: PlanFrame;
    /** PRODUCE: RM / PM POs whose order-by falls within this many days from today are part of the plan. */
    orderingDays?: number;
    /** Demand basis for the projection. Absent → the org's saved rate policy. */
    basis?: 'FORWARD' | 'BACKWARD';
    drrWindow?: number;
    /** Demand set up per channel. When present it replaces basis / drrWindow for the run. */
    demandPolicy?: DemandPolicy;
};

export type ActionDue = {
    actionId: string;
    planId: string;
    type: string;
    targetId: string | null;
    qty: number;
    triggerByDate: string | null;
    status: string;
    isOverdue: boolean;
    label: string;
};

export type ReplenishmentActionStatus =
    | 'PROPOSED'
    | 'PLANNED'
    | 'SCHEDULED'
    | 'PLACED'
    | 'FAILED'
    | 'CANCELLED';

export type ReplenishmentActionType =
    | 'CREATE_PO'
    | 'CREATE_WORK_ORDER'
    | 'CREATE_MATERIAL_ORDER'
    | 'CREATE_STN'
    | 'SEND_EMAIL'
    | 'CALL_EXTERNAL_API';

export type CalendarEvent = {
    actionId: string;
    planId: string;
    planItemId: string;
    date: string | null;
    type: ReplenishmentActionType;
    status: ReplenishmentActionStatus;
    title: string;
    sku: string | null;
    productName: string | null;
    qty: number;
    targetType: string | null;
    targetId: string | null;
    targetName: string | null;
    dependsOnActionIds: string[];
    referenceType: string | null;
    referenceId: string | null;
    isOverdue: boolean;
    isDueSoon: boolean;
    expectedDate: string | null;
    destination: string | null;
    needByDate: string | null;
    confirmedAt: string | null;
};

export type ActionVendorOption = {
    vendorId: string;
    vendorName: string;
    leadDays: number;
    placeBy: string | null;
    expectedDate: string | null;
};

export type ActionDetail = {
    actionId: string;
    type: ReplenishmentActionType;
    qty: number;
    status: ReplenishmentActionStatus;
    placeBy: string | null;
    expectedDate: string | null;
    destination: string | null;
    confirmedAt: string | null;
    peg: {
        planItemId: string;
        sku: string | null;
        productName: string | null;
        fgNeedQty: number;
        needByDate: string | null;
    };
    dependencies: {
        actionId: string;
        type: ReplenishmentActionType;
        status: ReplenishmentActionStatus;
        label: string;
        expectedDate: string | null;
    }[];
    vendorOptions: ActionVendorOption[];
};

export type CalendarSummary = {
    total: number;
    proposed: number;
    planned: number;
    scheduled: number;
    placed: number;
    failed: number;
    cancelled: number;
    needsApproval: number;
    approved: number;
    dueSoon: number;
    overdue: number;
};

export type ReplenishmentCalendar = {
    range: { from: string; to: string };
    dueSoonDays: number;
    summary: CalendarSummary;
    events: CalendarEvent[];
};

export const createReplenishmentPlan = async (
    payload: CreateReplenishmentPlanRequest,
): Promise<ReplenishmentPlan> => {
    return ReparoApi<any>({
        method: 'POST',
        url: '/replenishment-planning/plans',
        body: JSON.stringify(payload),
    });
};

export const getReplenishmentPlan = async (
    planId: string,
): Promise<ReplenishmentPlan> => {
    return ReparoApi<any>({
        method: 'GET',
        url: `/replenishment-planning/plans/${planId}`,
        body: null,
    });
};

export const listReplenishmentPlans = async (): Promise<ReplenishmentPlan[]> => {
    return ReparoApi<any>({
        method: 'GET',
        url: '/replenishment-planning/plans',
        body: null,
    });
};

export const approveReplenishmentPlan = async (
    planId: string,
    overrides?: { itemId: string; allocation: RecommendedAllocation[] }[],
): Promise<{ planId: string; plannedActions: number }> => {
    return ReparoApi<any>({
        method: 'POST',
        url: `/replenishment-planning/plans/${planId}/approve`,
        body: JSON.stringify({ overrides }),
    });
};

export const getReplenishmentCalendar = async (params?: {
    from?: string;
    to?: string;
    dueSoonDays?: number;
}): Promise<ReplenishmentCalendar> => {
    const query = new URLSearchParams();
    if (params?.from) query.set('from', params.from);
    if (params?.to) query.set('to', params.to);
    if (params?.dueSoonDays != null) query.set('dueSoonDays', String(params.dueSoonDays));
    const qs = query.toString();
    return ReparoApi<any>({
        method: 'GET',
        url: `/replenishment-planning/calendar${qs ? `?${qs}` : ''}`,
        body: null,
    });
};

export const seedReplenishmentCalendar = async (): Promise<{
    planId: string;
    actions: number;
}> => {
    return ReparoApi<any>({
        method: 'POST',
        url: '/replenishment-planning/dev/seed-calendar',
        body: null,
    });
};

export const confirmReplenishmentAction = async (
    actionId: string,
    overrides?: {
        vendorId?: string;
        vendorName?: string;
        qty?: number;
        // Force placement today; default schedules for the order-by date when it's in the future.
        placeNow?: boolean;
    },
): Promise<{ actionId: string; status: string; placeBy: string | null; expectedDate: string | null }> => {
    return ReparoApi<any>({
        method: 'POST',
        url: `/replenishment-planning/actions/${actionId}/confirm`,
        body: JSON.stringify(overrides ?? {}),
    });
};

/**
 * Group-confirm the review panel's selection. FG/RM PO actions are grouped into mother orders
 * (placeNow → one mother per vendor across windows; else one per place-by date); work orders /
 * STNs keep the schedule-or-place path. Returns how many actions were placed + the mother ids.
 */
export const confirmReplenishmentActionGroup = async (
    actionIds: string[],
    opts?: {
        placeNow?: boolean;
        /** Place even INFEASIBLE actions (RM late / not committed) — set after the reviewer
         *  acknowledges the warning. */
        override?: boolean;
        edits?: Array<{
            actionId: string;
            vendorId?: string;
            vendorName?: string;
            qty?: number;
            /** Which of the vendor's payment terms the order is placed on. */
            paymentTermsOptionId?: string;
        }>;
    },
): Promise<{
    placed: number;
    motherOrderIds: string[];
    /** Set when nothing was placed because some SKUs can't be produced in time; re-call with
     *  override:true to place anyway. */
    needsOverride?: boolean;
    blocked?: Array<{ actionId: string; sku: string | null; reason: string }>;
    blockedCount?: number;
}> => {
    return ReparoApi<any>({
        method: 'POST',
        url: `/replenishment-planning/actions/confirm-group`,
        body: JSON.stringify({ actionIds, ...(opts ?? {}) }),
    });
};

export const getReplenishmentActionDetail = async (
    actionId: string,
): Promise<ActionDetail> => {
    return ReparoApi<any>({
        method: 'GET',
        url: `/replenishment-planning/actions/${actionId}`,
        body: null,
    });
};

export const getReplenishmentDigest = async (
    planId: string,
): Promise<{ planId: string; digest: string }> => {
    return ReparoApi<any>({
        method: 'GET',
        url: `/replenishment-planning/plans/${planId}/digest`,
        body: null,
    });
};

// --- Track mode (placed supply: mother orders + work orders) ---

export interface TrackWindowBreakdownEntry {
    itemId: string;
    itemType: string;
    windowKey: string;
    windowStart: string | null;
    windowEnd: string | null;
    needByDate: string | null;
    quantity: number;
}

export interface TrackShipment {
    purchaseOrderId?: string;
    status?: string;
    plannedDate?: string | null;
    deliveryDate?: string | null;
    readinessDate?: string | null;
    departureDate?: string | null;
    arrivalDate?: string | null;
    totalUnits?: number;
    receivedUnits?: number;
    batchId?: string | null;
}

export interface TrackMother {
    id?: string;
    orderNumber?: string;
    vendorId?: string | null;
    vendorName?: string | null;
    orderType?: 'FIRM' | 'OPEN';
    status?: string;
    warehouseId?: string | null;
    orderDate?: string;
    orderedUnits?: number;
    inProductionUnits?: number;
    inTransitUnits?: number;
    openUnits?: number;
    receivedUnits?: number;
    windowBreakdown?: TrackWindowBreakdownEntry[];
    shipments?: TrackShipment[];
    lines?: Array<{ itemId?: string; description?: string | null; quantity?: number }>;
}

export interface TrackBatch {
    id: string;
    code: string;
    quantity: number;
    status: string;
    open: number;
}

export interface TrackWorkOrder {
    id: string;
    code: string;
    productId: string;
    quantity: number;
    dueDate: string;
    status: string;
    /** The production order this line sits under. Every work order has one. */
    productionOrderId: string | null;
    inProductionUnits: number;
    batchCount: number;
    batches: TrackBatch[];
}

/** The order handed to one manufacturer, with its SKU lines. */
export interface TrackProductionOrder {
    id: string;
    code: string;
    productionLocationType: string;
    productionLocationId: string;
    productionLocationName: string;
    expectedCompletionDate: string;
    status: string;
    lineCount: number;
    quantity: number;
    inProductionUnits: number;
    workOrders: TrackWorkOrder[];
}

export interface TrackStnItem {
    itemId: string;
    plannedQty: number;
    dispatchedQty: number;
    receivedQty: number;
}

/** A stock transfer: header (from → to) with its item lines. */
export interface TrackStn {
    id: string;
    code: string;
    fromNodeId: string;
    fromNodeName: string;
    toNodeId: string;
    toNodeName: string;
    itemType: string;
    status: string;
    createdAt: string;
    lineCount: number;
    plannedQty: number;
    dispatchedQty: number;
    receivedQty: number;
    items: TrackStnItem[];
}

export interface TrackOrigin {
    source: 'PLAN' | 'MANUAL';
    planId: string | null;
    servesWindows: string[];
}

export interface TrackOverview {
    summary: {
        motherCount: number;
        workOrderCount: number;
        productionOrderCount: number;
        stnCount: number;
        inProductionUnits: number;
        inTransitUnits: number;
        openUnits: number;
        receivedUnits: number;
    };
    mothers: TrackMother[];
    productionOrders: TrackProductionOrder[];
    workOrders: TrackWorkOrder[];
    stns: TrackStn[];
    originByMother: Record<string, TrackOrigin>;
    originByWorkOrder: Record<string, TrackOrigin>;
}

export interface ShipmentSuggestionRow {
    itemId: string;
    itemName: string | null;
    itemType: string;
    needByDate: string | null;
    windowKey: string | null;
    orderedQty: number;
    shippedQty: number;
    suggestedQty: number;
}

export interface ShipmentSuggestions {
    motherOrderId: string;
    warehouseId: string | null;
    rows: ShipmentSuggestionRow[];
}

/** On-demand shipment suggestions for a placed mother order, netted to pending. */
export const getShipmentSuggestions = async (
    motherId: string,
): Promise<ShipmentSuggestions> => {
    return ReparoApi<any>({
        method: 'GET',
        url: `/replenishment-planning/track/mothers/${motherId}/shipment-suggestions`,
        body: null,
    });
};

/** Create a production batch against a work order. */
export const createWorkOrderBatch = async (
    workOrderId: string,
    payload: {
        quantity: number;
        code?: string;
        notes?: string;
        /** What the batch is of. Defaults to the work order's product. */
        itemId?: string;
        manufacturingDate?: string;
        expiryDate?: string;
    },
): Promise<{ id: string; code: string }> => {
    return ReparoApi<any>({
        method: 'POST',
        url: `/work-orders/${workOrderId}/batches`,
        body: JSON.stringify(payload),
    });
};

/** Create the shipment for a batch (INCOMING PO with batch_id). */
export const createBatchShipment = async (
    batchId: string,
    payload: {
        quantity: number;
        plannedDate?: string | null;
        readinessDate: string; // GRD — mandatory; future OK
        deliveryDate?: string | null;
        warehouseId?: string | null;
        /**
         * Which work order this shipment fills. Required for a batch not carved against one;
         * given for a carved batch too, since one batch can serve several orders.
         */
        workOrderId?: string | null;
    },
): Promise<{ purchaseOrderId?: string }> => {
    return ReparoApi<any>({
        method: 'POST',
        url: `/work-orders/batches/${batchId}/shipment`,
        body: JSON.stringify(payload),
    });
};

export const getReplenishmentTrackOverview = async (): Promise<TrackOverview> => {
    return ReparoApi<any>({
        method: 'GET',
        url: `/replenishment-planning/track/overview`,
        body: null,
    });
};

export const getReplenishmentActionsDue = async (
    withinDays = 14,
): Promise<ActionDue[]> => {
    return ReparoApi<any>({
        method: 'GET',
        url: `/replenishment-planning/actions/due?withinDays=${withinDays}`,
        body: null,
    });
};

// --- Requirement graph (nodes + edges) ---

export type RequirementNodeType =
    | 'FG'
    | 'PRODUCTION'
    /** A semi-finished good at the hub, between the FG's pack run and its own factory run. */
    | 'SFG'
    | 'RM'
    | 'PM'
    | 'CAPACITY'
    | 'TRANSFER';

export type RecoveryStatus =
    | 'SATISFIED'
    | 'RECOVERED'
    | 'RECOVERABLE'
    | 'RECOVERABLE_WITH_RISK'
    | 'WINDOW_MISSED';

export type RequirementNode = {
    id: string;
    /** The plan run that last (re)computed this square. */
    planRunId?: string | null;
    nodeType: RequirementNodeType;
    itemKind: 'PRODUCT' | 'MATERIAL' | 'RESOURCE';
    itemId: string;
    sku: string | null;
    itemName: string | null;
    facilityId: string;
    facilityType: 'BRAND' | 'WAREHOUSE' | 'VENDOR' | 'MANUFACTURER';
    // The granularity this node was computed at. Nodes of different granularities
    // coexist in the graph, so the view must filter to the one being planned.
    periodType: PeriodType;
    windowKey: string;
    windowStart: string;
    windowEnd: string;
    requiredDate: string;
    requiredQty: number;
    targetQty: number | null;
    demandQty: number | null;
    openingQty: number;
    /** FG: TOTAL inbound for the window (in-transit + both in-production legs). RM: incoming POs. */
    inTransitQty: number;
    /** FG only: the FIRM-mother-remainder slice of inTransitQty. */
    inProductionPoQty: number;
    /** FG only: the open-work-order slice of inTransitQty. */
    inProductionWoQty: number;
    committedPlanQty: number;
    projectedAvailableQty: number;
    deficitQty: number;
    /** RM/PM: stock movable to the factory via a lane (0-lead = no STN, lead>0 = STN). Already netted out of deficit. */
    transferableQty: number;
    achievableQty: number;
    currentDoh: number | null;
    recoveryStatus: RecoveryStatus;
    latestDecisionDate: string | null;
    /** Unclamped ideal need/start — the true date, not raised to today. Table shows this. */
    idealRequiredDate: string | null;
    changeReason: string | null;
    impact: {
        residualDeficit: number;
        recoveryDate: string | null;
        deficitArea: number;
        periodAchievable: boolean;
    } | null;
    /** What the capacity pass did to this node; null when capacity never touched it. */
    capacityInfo: RequirementCapacityInfo | null;
    /** The channel slices behind the node; null = one slice, no priority. */
    demandBreakdown: DemandSlice[] | null;
    /** When the square was last (re)computed. */
    updatedAt?: string;
    status: 'OPEN' | 'CLOSED';
    // Catalog dimensions joined at read time (see backend getGraphView) — power the
    // product/material entity filters. PRODUCT nodes carry category/productGroup/tier/
    // isActive; MATERIAL nodes carry category/materialType. Null for the other kind.
    category: string | null;
    productGroup: string | null;
    tier: string | null;
    isActive: boolean | null;
    materialType: 'RM' | 'PM' | null;
    /** Unit of measure for MATERIAL nodes (e.g. 'm', 'kg', 'pcs'); null for products. */
    unit: string | null;
    // RM-readiness for PRODUCTION rows (null for FG/RM). Derived live on the backend from the
    // CONSUMES edges + each RM's committed instrument — powers the "blocked on RM" badge and the
    // "can't place until <date>" gate.
    unlock: ProductionUnlock | null;
    // Chain health for FG rows (null for PRODUCTION/RM). Derived live on the backend by rolling
    // the PRODUCTION/RM nodes beneath this FG up into it — `recoveryStatus` above is only the
    // FG's OWN feasibility, so this is the one to render in the status column.
    constraint: RequirementConstraint | null;
};

export type RequirementConstraint = {
    /** Worst status across this FG and everything beneath it — the honest status. */
    effectiveStatus: RecoveryStatus;
    /** The FG's own standalone status; differs from effectiveStatus when a child binds. */
    ownStatus: RecoveryStatus;
    bindingNode: {
        id: string;
        nodeType: RequirementNodeType;
        itemId: string;
        itemName: string | null;
        windowKey: string;
        recoveryStatus: RecoveryStatus;
        latestDecisionDate: string | null;
    } | null;
    /** One sentence naming what blocks it — the "why can't I act" answer. */
    reason: string | null;
};

export type ProductionBlockingReason =
    | 'NONE'
    | 'RM_UNCOMMITTED'
    | 'RM_IN_TRANSIT'
    | 'RM_UNRECOVERABLE';

export type ProductionUnlock = {
    blockingReason: ProductionBlockingReason;
    /** Earliest date the work order can be placed (RM arrival); null when placeable now or blocked with no ETA. */
    unlockDate: string | null;
    /** Earliest it could be placed if every un-ordered RM went out today — the date behind "after RM lands". */
    earliestUnlockDate: string | null;
    blockedBy: Array<{
        materialId: string;
        itemName: string | null;
        deficitQty: number;
        committed: boolean;
        expectedDate: string | null;
        etaIfOrderedToday: string | null;
        idealDate: string | null;
        worstDate: string | null;
        unrecoverable: boolean;
    }>;
};

export type RequirementEdge = {
    id: string;
    parentNodeId: string;
    childNodeId: string;
    relationship: 'NEEDS' | 'CONSUMES' | 'DEPENDS_ON' | 'PRODUCED_BY' | 'TRANSFERRED_BY';
    qtyPerParent: number;
};

export type RequirementGraph = {
    nodes: RequirementNode[];
    edges: RequirementEdge[];
};

export const getRequirementGraph = async (
    windowKey?: string,
): Promise<RequirementGraph> => {
    const qs = windowKey ? `?windowKey=${encodeURIComponent(windowKey)}` : '';
    return ReparoApi<any>({
        method: 'GET',
        url: `/replenishment-planning/requirement-graph${qs}`,
        body: null,
    });
};

// --- Instrument timeline (bucketed) ---

export type TimelineBucketKey =
    | 'OVERDUE'
    | 'TODAY'
    | 'THIS_WEEK'
    | 'THIS_MONTH'
    | 'LATER';

/** One FG requirement driving an RM/PRODUCTION node — the "why is this here?" answer. */
export type RequirementDriver = {
    fgNode: RequirementNode;
    contributedQty: number;
};

/**
 * Must be fetched per node rather than derived from getRequirementGraph: that endpoint
 * scopes edges to the selected window, and a child sits in an EARLIER window than its FG
 * parent whenever the lead is non-zero — so the driving edge is absent from the payload.
 */
export const getRequirementDrivers = async (
    nodeId: string,
): Promise<RequirementDriver[]> => {
    return ReparoApi<any>({
        method: 'GET',
        url: `/replenishment-planning/requirements/${encodeURIComponent(nodeId)}/drivers`,
        body: null,
    });
};

export type TimelineItem = {
    actionId: string;
    requirementNodeId: string | null;
    type: ReplenishmentActionType;
    status: ReplenishmentActionStatus;
    title: string;
    sku: string | null;
    productName: string | null;
    qty: number;
    fromNodeType: string | null;
    fromNodeId: string | null;
    toNodeType: string | null;
    toNodeId: string | null;
    placeBy: string | null;
    placeByEarliest: string | null;
    placeByLatest: string | null;
    expectedDate: string | null;
    selectedVendorId: string | null;
    vendorCandidates: ActionVendorOption[];
    dependsOnActionIds: string[];
    isOverdue: boolean;
    /** Planning window this instrument belongs to — the basis for "this week" as a bucket. */
    windowKey: string | null;
    windowStart: string | null;
    windowEnd: string | null;
    /** FG requirement windows this instrument ultimately serves (walked up the edges). */
    fgWindowKeys: string[];
    /** FG deficit still rescued by placing this today, summed over the FG roots it feeds. */
    recoverableFgQty: number;
    /** recoverableFgQty > 0 — whether a past-due order is still worth placing. */
    stillWorthPlacing: boolean;
    /** The requirement node's ideal date and its same-axis worst case. */
    idealDate: string | null;
    worstCaseDate: string | null;
    /** Order → arrival lead, for the estimated arrival if placed today. */
    leadDays: number | null;
    /** When the DOCUMENT must be placed — ideal and worst case, both on the placing axis. */
    placeByIdeal: string | null;
    placeByWorst: string | null;
    /** Node recovery status — the shared source of urgency ('RECOVERABLE' = on track). */
    recoveryStatus: string | null;
    /** RM-readiness for a work order (null for other instrument types). Blocks placement. */
    unlock: ProductionUnlock | null;
    /** Unit for material orders (RM PO / STN) so the panel shows "220 (m)"; null otherwise. */
    unit: string | null;
};

export type TimelineBucket = {
    key: TimelineBucketKey;
    label: string;
    count: number;
    items: TimelineItem[];
};

/**
 * What instrument types this brand's supply chain can produce, from its lane topology.
 * Drives which summary cards exist at all — a card reading 0 means "nothing to place this
 * week", an absent card means "this brand never does that".
 */
export type SupplyChainCapability = {
    canBuyFg: boolean;
    canProduce: boolean;
    canBuyMaterial: boolean;
    canTransfer: boolean;
};

export type InstrumentTimeline = {
    generatedAt: string;
    buckets: TimelineBucket[];
    capability: SupplyChainCapability;
};

// ─── Demand setup (per channel) ─────────────────────────────────────────────

export type DemandSource = 'DRR' | 'FORECAST' | 'MAX_DRR_FORECAST' | 'OPEN_PO' | 'MAX_OPEN_PO_DRR' | 'COMMITTED' | 'DRR_PLUS_COMMITTED' | 'EXCLUDE';
export type DrrCombine = 'AVG' | 'MAX' | 'MIN';

export type DemandChannelRule = {
    channelKey: string;
    source: DemandSource;
    /** 1 = served first when supply is short. */
    priority?: number;
};

export type DemandPolicy = {
    /** Run-rate windows, in days, blended into one rate. */
    drrWindows: number[];
    drrCombine: DrrCombine;
    /** What a channel without its own rule reads. */
    defaultSource: DemandSource;
    channels: DemandChannelRule[];
};

/** One channel's signals side by side. Every rate is units per day, summed over SKUs. */
export type DemandPreviewChannel = {
    channelKey: string;
    fulfillmentMode: string | null;
    drrByWindow: Record<string, number>;
    blendedDrr: number;
    /** Null when the channel has no forecast uploaded. */
    forecastPerDay: number | null;
    openPoUnits: number;
    committedUnits: number;
    committedSkus: number;
    isB2b: boolean;
    openPoSkus: number;
    source: DemandSource;
    suggestedSource: DemandSource;
    demandPerDay: number;
    priority: number | null;
};

export type DemandPreview = {
    horizonDays: number;
    policy: DemandPolicy;
    channels: DemandPreviewChannel[];
    totalDemandPerDay: number;
};

/** What a demand policy would plan per channel over the next 30 days. Read-only. */
export const previewDemand = async (policy?: DemandPolicy): Promise<DemandPreview> =>
    ReparoApi<any>({
        method: 'POST',
        url: '/replenishment-planning/demand-preview',
        body: JSON.stringify(policy ? { policy } : {}),
    });

// ─── Production capacity ────────────────────────────────────────────────────

export type CapacityTiming = 'ON_TIME' | 'EARLY' | 'LATE' | 'MISSED';

export type RequirementCapacityInfo = {
    /** The worst timing among the quantity on the node. */
    timing: CapacityTiming;
    /** PRODUCTION: units the cap moved onto this node. */
    shiftedQty: number;
    /** PRODUCTION: the primary factory the quantity was moved off; null when it is the primary. */
    overflowFrom: string | null;
    /** PRODUCTION: units holding no factory time — no material in the horizon (RM) or no free day (CAPACITY). */
    blockedBy?: 'CAPACITY' | 'RM' | null;
    blockedQty?: number;
    /** RM-blocked: the material id that ran out first. */
    blockedMaterial?: string | null;
    /** FG: units no capable factory could take before the plan's horizon ended. */
    unplacedQty: number;
    placements?: {
        manufacturerId: string;
        windowKey: string;
        startDate: string;
        qty: number;
        timing: CapacityTiming;
        overflowFrom: string | null;
    }[];
};

export type CapacityLoadWindow = {
    windowKey: string;
    windowStart: string;
    windowEnd: string;
    capacity: number;
    committed: number;
    planned: number;
    free: number;
};

export type CapacityLoadFactory = {
    manufacturerId: string;
    manufacturerName: string;
    dailyCapacity: number;
    workingDays: number[] | null;
    windows: CapacityLoadWindow[];
};

export type CapacityLoad = {
    factories: CapacityLoadFactory[];
    unplaced: { productId: string; sku: string | null; productName: string | null; windowKey: string; qty: number }[];
    moved: { earlyQty: number; lateQty: number; overflowQty: number; unplacedQty: number; rmBlockedQty: number };
};

/** Factory load for the current plan — empty `factories` when no manufacturer has a capacity set. */
export const getCapacityLoad = async (): Promise<CapacityLoad> =>
    ReparoApi<any>({
        method: 'GET',
        url: '/replenishment-planning/capacity-load',
        body: null,
    });

export const getInstrumentTimeline = async (): Promise<InstrumentTimeline> => {
    return ReparoApi<any>({
        method: 'GET',
        url: '/replenishment-planning/instruments/timeline',
        body: null,
    });
};

// ─── Inventory health (the reorder-point landing view) ─────────────────────
// A pure read of today's position — no plan is created, nothing is persisted, and the
// planning engine is not run. `shortfall` here is deliberately NOT the plan's
// `requirement`: this nets nothing but stock + inbound against the reorder point,
// where the plan also nets a period's forecast consumption.

export type RatePolicyBasis = 'FORWARD' | 'BACKWARD';

export type InventoryHealthInbound = {
    /** Shipped against a PO, GRD set — physically moving. */
    inTransitPo: number;
    /** Shipped on an internal stock-transfer note. */
    inTransitStn: number;
    /** On a FIRM mother order, not yet shipped — the vendor is still making it. */
    inProductionPo: number;
    /** Open work-order batch quantity — our own / contract production. */
    inProductionWo: number;
    /** PENDING — committed, nothing started. Unshipped balance of an OPEN (blanket) order. */
    pendingPo: number;
    /** Un-batched remainder of a committed production order. Always 0 for materials. */
    pendingWo: number;
    /**
     * The two columns the grid shows, derived server-side from the four legs above.
     * IN TRANSIT = physically moving (PO shipments past their GRD + STNs).
     * OPEN = committed but not yet moving — the two in-production legs (a run is under way)
     * plus the two pending legs (committed, nothing started).
     */
    inTransit: number;
    open: number;
    total: number;
};

/**
 * Which placement a row supports. BUY → purchase order, MAKE → production/work order,
 * UNMAPPED → nothing can be placed (no vendor mapped). Mirrors the backend enum and is
 * derived from the suggested vendor's MANUFACTURER capability, so it agrees with the
 * instrument the planning engine would generate for the same SKU.
 */
export type InventorySourcingModel = 'BUY' | 'MAKE' | 'UNMAPPED';

/**
 * Which rung of the chain decided a SKU's reorder DRR window: an override on this SKU, its
 * tier's rule, the merchant default, or the org's sales lookback showing through.
 *
 * SKU is the only one the grid lets you set directly; the rest are inherited and the badge says
 * so, because "this 7d is a decision somebody made about this SKU" and "this 7d is what
 * everything defaults to" lead to completely different next actions.
 */
/**
 * Where the reorder point's day-count came from. On past sales it is a LOOKBACK chosen down
 * the SKU → tier → merchant → org chain; on forecast it is `COVERAGE` — the horizon
 * `lead + safety` itself, which nothing chooses and nothing can override.
 */
export type RopWindowSource =
    | 'SKU'
    | 'TIER'
    | 'CONFIG_DEFAULT'
    | 'ORG_FALLBACK'
    | 'COVERAGE';

export type InventoryHealthRow = {
    productId: string;
    sku: string | null;
    productName: string | null;
    category: string | null;
    productGroup: string | null;
    /** Product rows only — the third product grouping dimension. */
    tier: string | null;
    /** Material rows only. */
    materialType: string | null;
    /** Material rows only — quantities are not unit-uniform (m / kg / pcs). */
    unit: string | null;
    sourcingModel: InventorySourcingModel;
    /** On-hand across active internal warehouses PLUS `stockTransfer` (products). */
    currentStock: number;
    /**
     * Products only: the part of `currentStock` on an open transfer between two of the brand's
     * own warehouses (dispatched, not yet received). Null when the brand has none.
     */
    stockTransfer?: number | null;
    inbound: InventoryHealthInbound;
    /** Always `ropDrr × (reorderLeadTimeDays + reorderSafetyDays)` — every threshold on the
     *  grid can be reproduced from the columns beside it. */
    reorderPoint: number;
    /** Tier the rule was read at, or null when the SKU is untiered. */
    reorderTier: string | null;
    /** The terms behind the number, so the UI can explain it. Target cover is NOT among them —
     *  that is the plan's concept, kept separate on purpose. */
    reorderLeadTimeDays: number;
    reorderSafetyDays: number;
    /** The DRR window the reorder point is SIZED on, and which rung of the chain chose it.
     *  Independent of the window being viewed — that independence is the point: the threshold
     *  holds still while the DRR control is dragged around. */
    ropWindowDays: number;
    ropWindowSource: RopWindowSource;
    /** The run rate at `ropWindowDays` — the rate the reorder point was built from. Differs
     *  from `drr` whenever the policy window and the view window differ, which is exactly when
     *  the gap between them is the argument for changing this SKU's window. */
    ropDrr: number;
    /** Run rate at the VIEW's window — what the DRR column and cover days are built on. */
    drr: number;
    /** The same rate one window earlier, and the move to `drr`. Both null when there is no
     *  honest comparison (forecast basis, or nothing sold in the prior window). The raw prior
     *  rate is carried so grouped rows can recompute the group's move from summed current and
     *  summed prior rather than averaging percentages. */
    drrPrevious: number | null;
    drrDeltaPct: number | null;
    shortfall: number;
    coverDays: number | null;
    vendorId: string | null;
    vendorName: string | null;
    moq: number | null;
    /** currentStock split into two disjoint buckets that sum back to it. INTERNAL wins: a
     *  factory that is also one of our warehouses counts as internal, so `atManufacturer`
     *  means an EXTERNAL manufacturing facility. */
    stockInternal: number;
    stockAtManufacturer: number;
    /** The in-transit leg split on the same axis, same rule. Sums to the in-transit leg. */
    inTransitInternal: number;
    inTransitAtManufacturer: number;
    /** Material rows only — today's FG shortfall consuming this material, and the BOM-implied
     *  material quantity that shortfall represents. */
    fgDeficit: number;
    rmRequired: number;
};

export type InventoryHealthResponse = {
    data: InventoryHealthRow[];
    targetDaysOfCover: number;
    basis: RatePolicyBasis;
    drrWindowDays: number;
    /**
     * The direction REORDER POINTS were sized in — the brand's stated policy, not `basis`
     * above. Optional so a backend that predates the setting still renders.
     */
    ropBasis?: RatePolicyBasis;
    isRopBasisConfigured?: boolean;
    belowReorderPoint: number;
    total: number;
};

export type InventoryHealthRequest = {
    productIds?: string[];
    categories?: string[];
    productGroups?: string[];
    search?: string;
    basis?: RatePolicyBasis;
    drrWindow?: number;
    /** PRODUCT (default) = finished goods; MATERIAL = the raw/packaging view. */
    itemType?: 'product' | 'material';
    /** Narrow to one supply model. Absent ⇒ every row. */
    sourcingModel?: InventorySourcingModel;
};

export const getInventoryHealth = async (
    payload: InventoryHealthRequest = {},
): Promise<InventoryHealthResponse> => {
    return ReparoApi<any>({
        method: 'POST',
        url: '/replenishment-planning/inventory-health',
        body: JSON.stringify(payload),
    });
};

/**
 * The DRR trend, fetched SEPARATELY from inventory health and merged in when it lands.
 *
 * Inline it cost ~2.2s — the previous window needs its own pass through the stockout gate, which
 * is per-window by definition and cannot share work with the current one. That is too much to
 * spend on a landing view where nothing is readable until the response arrives. The DRR figure
 * is correct without the arrow; the arrow annotates it a moment later.
 */
export type DrrTrendRequest = {
    productIds?: string[];
    basis?: RatePolicyBasis;
    drrWindow?: number;
    categories?: string[];
    productGroups?: string[];
    search?: string;
};

export type DrrTrendResponse = {
    /** Only SKUs with real prior demand. An absent SKU sold nothing then — rendered as "new". */
    trends: Array<{
        productId: string;
        drrPrevious: number;
        drrDeltaPct: number | null;
    }>;
    /** Echoed so a response that no longer matches the grid can be discarded. */
    basis: RatePolicyBasis;
    drrWindowDays: number;
};

export const getDrrTrend = async (
    payload: DrrTrendRequest,
): Promise<DrrTrendResponse> => {
    return ReparoApi<any>({
        method: 'POST',
        url: '/replenishment-planning/inventory-health/drr-trend',
        body: JSON.stringify(payload),
    });
};

/**
 * The reorder-window editor's what-if: run rates for a set of SKUs across candidate windows.
 *
 * Fetched LAZILY, once, when the editor is first opened — not folded into the health response.
 * Most sessions never touch the control, and making every page load pay for a rate query per
 * candidate window would tax the landing view for a feature nobody had asked for yet.
 */
export type RopDrrWindowRatesRequest = {
    productIds: string[];
    windows: number[];
    basis?: RatePolicyBasis;
    categories?: string[];
    productGroups?: string[];
    search?: string;
};

export type RopDrrWindowRate = {
    productId: string;
    windowDays: number;
    /** Units per day. 0 means the SKU sold nothing over that window — not an error. */
    dailyRate: number;
};

export type RopDrrWindowRatesResponse = {
    rates: RopDrrWindowRate[];
    basis: RatePolicyBasis;
};

export const getRopDrrWindowRates = async (
    payload: RopDrrWindowRatesRequest,
): Promise<RopDrrWindowRatesResponse> => {
    return ReparoApi<any>({
        method: 'POST',
        url: '/replenishment-planning/inventory-health/drr-window-rates',
        body: JSON.stringify(payload),
    });
};

/**
 * Set or clear per-SKU reorder windows. `windowDays: null` CLEARS the override, handing the SKU
 * back to its tier rule or the merchant default — that is a real instruction, not a missing
 * value, and it is the only honest encoding of "reset to default".
 */
export type SetRopDrrWindowsRequest = {
    entries: Array<{ productId: string; windowDays: number | null }>;
};

export type SetRopDrrWindowsResponse = { saved: number; cleared: number };

export const setRopDrrWindows = async (
    payload: SetRopDrrWindowsRequest,
): Promise<SetRopDrrWindowsResponse> => {
    return ReparoApi<any>({
        method: 'POST',
        url: '/replenishment-planning/inventory-health/rop-drr-windows',
        body: JSON.stringify(payload),
    });
};

export type InventoryHealthInboundLine = {
    /** PO id (in transit), mother-order NUMBER (in production via PO), or work-order code.
     *  Null only when no document exists behind the quantity. */
    reference: string | null;
    /** Mother order the in-transit shipment came from. Null for standalone POs, STNs and
     *  every in-production line (those name the mother order in `reference` itself). */
    motherReference: string | null;
    source: 'PO' | 'STN' | 'WO';
    /** IN_TRANSIT = moving; IN_PRODUCTION = a run is under way; PENDING = committed,
     *  nothing started. The three partition every open unit exactly once. */
    state: 'IN_TRANSIT' | 'IN_PRODUCTION' | 'PENDING';
    status: string | null;
    /** Expected arrival. Null when the instrument carries no usable date. */
    expectedDate: string | null;
    quantity: number;
};

export type InventoryHealthInboundDetail = {
    productId: string;
    lines: InventoryHealthInboundLine[];
    total: number;
};

/** Every open inbound line for one SKU, with the date each lands. */
export const getInventoryHealthInbound = async (
    productId: string,
    itemType: 'product' | 'material' = 'product',
): Promise<InventoryHealthInboundDetail> => {
    return ReparoApi<any>({
        method: 'GET',
        url: `/replenishment-planning/inventory-health/${encodeURIComponent(productId)}/inbound?itemType=${itemType}`,
        body: null,
    });
};

// ─── Replenishment config (tier target DoH + reorder rules) ────────────────
// The tier DEFINITION lives on the org (settings → Product Tier Config) and is applied to
// every scope at recalculation time. This config only names WHICH precomputed scope
// replenishment reads, and what each tier's numbers are.

/** Which denominator tiers are read against. Not a definition — a choice among precomputed scopes. */
export type ReplenishmentTierScope = 'BRAND' | 'PRODUCT_GROUP' | 'CATEGORY';

export type TierReorderRule = {
    /** Overrides the lane-resolved lead time. Null ⇒ use the resolved one. */
    leadTimeDays: number | null;
    /** Additive buffer on top of the tier's target DoH, in days. Null ⇒ the config default. */
    safetyDays: number | null;
    /** The DRR lookback this tier's reorder points are SIZED on. Null ⇒ the config default,
     *  then the org's sales lookback. Not the window being viewed — that one re-bases the
     *  screen, this one re-bases what the brand buys against. */
    drrWindowDays: number | null;
};

export type ReplenishmentConfig = {
    tierScope: ReplenishmentTierScope;
    defaultTargetDohDays: number;
    /** Per-tier days of cover. A tier with no entry falls back to defaultTargetDohDays. */
    tierTargetDohDays: Record<string, number> | null;
    reorderRules: Record<string, TierReorderRule> | null;
    defaultReorderSafetyDays: number;
    /** The EFFECTIVE default reorder DRR window — the saved value, or the org's sales lookback
     *  showing through. A window always applies; the question is only whose. */
    defaultRopDrrWindowDays: number | null;
    /** False when the number above is inherited rather than chosen here, so the editor can
     *  show it as a placeholder instead of pinning the merchant to a value nobody picked. */
    isRopDrrWindowConfigured: boolean;
    /** Which DIRECTION reorder points read demand in — past sales over the window above, or
     *  forecast over it. Merchant-wide, unlike the window, which is also per tier and per SKU.
     *  Effective, never null: the org's `lookup` shows through when nothing is saved here. */
    ropDrrBasis: RatePolicyBasis;
    /** False when the basis above is the org default rather than a choice made here. */
    isRopDrrBasisConfigured: boolean;
    /** The brand's demand setup by channel; null = the single basis. */
    demandPolicy: DemandPolicy | null;
    /** Factory split and vendor rules; null = primary first, overflow by mapping order. */
    sourcingRules: SourcingRules | null;
    /** Tier labels the brand's classifier actually emits — the rows the editor renders. */
    tierLabels: string[];
    /** False when nothing is saved yet and these are the effective defaults. */
    isConfigured: boolean;
};

export const getReplenishmentConfig = async (): Promise<ReplenishmentConfig> => {
    return ReparoApi<any>({
        method: 'GET',
        url: '/replenishment-planning/config',
        body: null,
    });
};

/** Partial — an omitted field is left as-is server-side rather than cleared. */
export const updateReplenishmentConfig = async (
    payload: Partial<
        Pick<
            ReplenishmentConfig,
            | 'tierScope'
            | 'defaultTargetDohDays'
            | 'tierTargetDohDays'
            | 'reorderRules'
            | 'defaultReorderSafetyDays'
            | 'defaultRopDrrWindowDays'
            | 'ropDrrBasis'
            | 'demandPolicy'
        >
    >,
): Promise<ReplenishmentConfig> => {
    return ReparoApi<any>({
        method: 'PUT',
        url: '/replenishment-planning/config',
        body: JSON.stringify(payload),
    });
};

// ─── Production RM allotment ────────────────────────────────────────────────
// The second of the two planning steps: the plan says how much to make, this says who gets
// the shared material and how many units each run can ACTUALLY make from it.

export type RmAllocationPriority = 'NEED_BY' | 'QTY' | 'DRR' | 'DOH';

/** Order within a priority tie — decides most of the allocation, since a weekly bucket gives
 *  nearly every run the same need-by date. 'SMALLEST' protects little runs from being starved. */
export type RmAllocationTieBreak = 'QTY' | 'DRR' | 'SMALLEST' | 'DOH';

export type RmAllocationMaterial = {
    materialId: string;
    itemName: string | null;
    unit: string | null;
    /** BOM quantity per finished unit. */
    perUnit: number;
    requiredQty: number;
    allocatedQty: number;
    shortQty: number;
    /** Total on the floor before ANY run drew — the same for every run on this material. */
    onHandQty: number;
    /** What was LEFT when this run took its turn: onHandQty minus what earlier runs took. */
    inStockQty: number;
    /** Inbound landing on or before this run's start — counts towards it. */
    expectedQty: number;
    /** Inbound landing after this run's start — real, but too late for it. */
    delayedQty: number;
    /** When the next delayed delivery lands; null when nothing is outstanding. */
    nextArrivalDate: string | null;
    /** inStock + expected — everything this run may actually draw on. */
    availableQty: number;
    /** What was still free when THIS run took its turn in the priority order. */
    freeAtTurnQty: number;
    /** What is left after this run drew. */
    remainingQty: number;
    /** True on the one component that actually caps the run. */
    isBottleneck: boolean;
};

export type RmAllocationRun = {
    productionNodeId: string;
    itemId: string;
    sku: string | null;
    itemName: string | null;
    facilityId: string;
    windowKey: string;
    requestedQty: number;
    /** True when you've settled this run and it draws ahead of the queue. */
    isPinned: boolean;
    /** Units the material on hand can cover — partial runs, not a yes/no. */
    makeableQty: number;
    needByDate: string;
    latestDecisionDate: string | null;
    drrPerDay: number;
    /** Worst current days-of-cover across the FGs this run serves; null when unknown. */
    dohDays: number | null;
    limitingMaterialId: string | null;
    limitingMaterialName: string | null;
    /** Capped by contention (material exists, earlier runs took it), not by absence. */
    limitedByContention: boolean;
    /** Earliest date this run could start with every component present; null if ordering is the only fix. */
    feasibleStartDate: string | null;
    /** Units makeable if it started on feasibleStartDate instead. */
    makeableAtFeasibleStart: number;
    /** The material capping it AFTER everything lands — usually not the same as limitingMaterial. */
    laterLimitingMaterialId: string | null;
    laterLimitingMaterialName: string | null;
    /** Last possible start already passed — neither waiting nor ordering rescues it. */
    pastLastStart: boolean;
    /** When it should have begun. */
    idealStartDate: string;
    /** Last date it can begin and still make the FG in time. */
    lastStartDate: string | null;
    /** Whether moving to feasibleStartDate still lands the FG in time (production lead included). */
    isRecoverable: boolean;
    materials: RmAllocationMaterial[];
};

export type RmAllocationPool = {
    rmNodeId: string;
    materialId: string;
    itemName: string | null;
    facilityId: string;
    windowKey: string;
    onHandQty: number;
    inTransitQty: number;
    transferableQty: number;
    availableQty: number;
    demandedQty: number;
    allocatedQty: number;
    shortQty: number;
    /** How many distinct SKUs draw on this material in the window. */
    skuCount: number;
    /** Shared between SKUs AND oversubscribed — the only case needing a decision. */
    contested: boolean;
};

export type RmAllocation = {
    runs: RmAllocationRun[];
    pools: RmAllocationPool[];
};

export const getRmAllocation = async (payload: {
    windowKey?: string;
    /**
     * The production nodes competing for the material — send the runs actually on screen.
     * Takes precedence over `windowKey`, which can only scope by the runs' own window.
     */
    nodeIds?: string[];
    priority?: RmAllocationPriority;
    tieBreak?: RmAllocationTieBreak;
    /** Per-run quantity overrides, keyed by production node id. */
    overrides?: Record<string, number>;
    /** Runs already settled, in settle order. They draw first and skip the sort. */
    pinned?: string[];
}): Promise<RmAllocation> => {
    return ReparoApi<any>({
        method: 'POST',
        url: '/replenishment-planning/production/rm-allocation',
        body: JSON.stringify(payload),
    });
};

export type DemandSlice = {
    channelKey: string;
    priority: number | null;
    demandQty: number;
    deficitQty: number;
    /** PRODUCTION: units of this slice that hold factory time. */
    placedQty?: number;
};

// ─── Sourcing rules (factory split, vendor choice) ──────────────────────────

export type SourcingMode = 'PRIMARY_ONLY' | 'OVERFLOW' | 'SPLIT';

export type SourcingRule = { mode: SourcingMode; order: string[]; ratios?: Record<string, number> };

/** Produce an FG outright at its factory, or make its SFG(s) and pack them at the hub. */
export type ProductionStage = 'FG' | 'SFG';

export type SourcingRules = {
    factories?: Record<string, SourcingRule>;
    vendors?: Record<string, SourcingRule>;
    stages?: Record<string, ProductionStage>;
};

export type SourcingQuestion = {
    /** STAGE: options are the two stages ('FG' / 'SFG'); no mode applies. */
    kind: 'FACTORY' | 'VENDOR' | 'STAGE';
    itemId: string;
    itemName: string | null;
    sku: string | null;
    options: Array<{ id: string; name: string; leadDays: number | null; capacityPerDay: number | null }>;
    current: SourcingRule | null;
    /** FACTORY question on a semi-finished good: the in-scope FGs packed from it. */
    sfgOf?: Array<{ productId: string; sku: string | null }>;
};

/**
 * What the engine needs answered before it can plan. With an intent, only for the SKUs that
 * objective would plan (plus their SFGs and materials); without, the whole catalogue.
 */
export const getSourcingQuestions = async (intent?: PlanQuestionIntent): Promise<{ questions: SourcingQuestion[]; answered: number }> =>
    intent
        ? ReparoApi<any>({ method: 'POST', url: '/replenishment-planning/questions/inputs', body: JSON.stringify({ intent }) })
        : ReparoApi<any>({ method: 'GET', url: '/replenishment-planning/questions/inputs', body: null });

/** Partial: keys given replace, keys omitted stay. */
export const saveSourcingRules = async (patch: SourcingRules): Promise<SourcingRules> =>
    ReparoApi<any>({ method: 'PUT', url: '/replenishment-planning/questions/sourcing-rules', body: JSON.stringify(patch) });

// ─── Planning questions (Ask) ───────────────────────────────────────────────

export type PlanQuestionObjective = 'PRODUCE' | 'ORDER_RM' | 'SHORTFALL';

export type PlanQuestionPeriod =
    | { kind: 'DATES'; start: string; end: string }
    | { kind: 'RELATIVE'; offsetDays: number; lengthDays: number };

/** What the user asked, as structure. The sentence is parsed into this and shown back before a run. */
export type PlanQuestionIntent = {
    objective: PlanQuestionObjective;
    period: PlanQuestionPeriod;
    periodType: PeriodType;
    levelDays?: number | null;
    replenishToDays?: number | null;
    orderingDays?: number | null;
};

export type PlanQuestionRow = {
    item_id: string;
    sku: string | null;
    item_name: string | null;
    qty: number;
    scheduled_qty: number;
    facility: string | null;
    date: string | null;
    status: string | null;
    note: string | null;
};

export type PlanQuestionAnswer = {
    planId: string;
    objective: PlanQuestionObjective;
    periodStart: string | null;
    periodEnd: string | null;
    headline: string;
    data: PlanQuestionRow[];
    total: number;
    totalQty: number;
    computedAt: string;
};

/** Ask the engine a question. Runs the plan for the squares it needs; the answer is rows plus a headline. */
export const answerPlanQuestion = async (
    intent: PlanQuestionIntent,
    allotment?: { priority: 'NEED_BY' | 'QTY' | 'DRR' | 'DOH'; tieBreak: 'QTY' | 'DRR' | 'SMALLEST' | 'DOH' },
    /** This run's factory / vendor / stage answers, laid over the brand's saved defaults. */
    sourcingRules?: SourcingRules,
): Promise<PlanQuestionAnswer> =>
    ReparoApi<any>({
        method: 'POST',
        url: '/replenishment-planning/questions/answer',
        body: JSON.stringify({
            intent,
            ...(allotment ? { allocationPriority: allotment.priority, allocationTieBreak: allotment.tieBreak } : {}),
            ...(sourcingRules ? { sourcingRules } : {}),
        }),
    });

// ─── Saved questions (a scheduled alert of type REPLENISHMENT_QUESTION) ─────

export type SavedQuestionFrequency = 'DAILY' | 'WEEKLY';

export type SavedQuestion = {
    alertId: string;
    alertLabel: string | null;
    context: { name?: string; sentence?: string; intent?: PlanQuestionIntent } | null;
    frequency: SavedQuestionFrequency | null;
    triggerTime: string | null;
    triggerDayOfWeek: number | null;
    isScheduled: boolean;
    lastEvaluatedAt: string | null;
    nextEvaluationAt: string | null;
};

export const listSavedQuestions = async (): Promise<SavedQuestion[]> => {
    const alerts = await ReparoApi<any>({ method: 'GET', url: '/user-alerts', body: null });
    return (alerts as Array<SavedQuestion & { alertType: string }>).filter((a) => a.alertType === 'REPLENISHMENT_QUESTION');
};

export const saveQuestion = async (input: {
    name: string;
    sentence: string;
    intent: PlanQuestionIntent;
    frequency: SavedQuestionFrequency;
    /** HH:mm, in the brand's schedule timezone. */
    triggerTime: string;
    /** 0 = Sunday … 6 = Saturday; WEEKLY only. */
    triggerDayOfWeek?: number | null;
}): Promise<SavedQuestion> =>
    ReparoApi<any>({
        method: 'POST',
        url: '/user-alerts',
        body: JSON.stringify({
            alertType: 'REPLENISHMENT_QUESTION',
            alertLabel: input.name,
            context: { name: input.name, sentence: input.sentence, intent: input.intent },
            frequency: input.frequency,
            triggerTime: input.triggerTime,
            ...(input.frequency === 'WEEKLY' ? { triggerDayOfWeek: input.triggerDayOfWeek ?? 1 } : {}),
            isScheduled: true,
        }),
    });

export const deleteSavedQuestion = async (alertId: string): Promise<void> => {
    await ReparoApi<any>({ method: 'DELETE', url: `/user-alerts/${encodeURIComponent(alertId)}`, body: null });
};
